import React, { useCallback, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography
} from '@mui/material'
import { useMutation, useQuery } from '@tanstack/react-query'
import B2bTDPAPI from 'api/b2b/TdpApi'
import WalletAdjustmentApi, {
  IWalletAdjustmentBackendData
} from 'api/b2b/WalletAdjustment'
import OrbitSession from 'hooks/useCookies'

import WalletAdjustmentList from './WalletAdjustmentList'

interface Company {
  tenant_id: string
  company_name: string
  id: string
}

interface TenantData {
  data: Company[]
}

interface CompanyOption {
  label: string
  value: string
  companyId: string
}

interface FormData {
  companyName: CompanyOption | null
  fromDate: string
  toDate: string
}
export type WalletAdjustmentTableDataType = {
  company_name: string
  transaction_date_time: string
  type: string
  reference_number: string
  email: string
  comment: string
  note: string
  adjustment_type: string
  amount: number
  created_by: string
  created_on: string
  updated_by: string
  updated_on: string
}

export default function SearchWallet() {
  const navigate = useNavigate()
  const [tableData, setTableData] = useState<WalletAdjustmentTableDataType[]>(
    []
  )
  // Search form
  const {
    control: searchControl,
    handleSubmit: handleSearchSubmit,
    reset: resetSearch,
    watch
    // formState: { errors: error }
  } = useForm<FormData>({
    defaultValues: {
      companyName: null,
      fromDate: '',
      toDate: ''
    }
  })

  const {
    data: tenantData,
    isLoading: isTenantLoading,
    isError: tenantFetchError,
    refetch: refetchTenant
  } = useQuery<TenantData>(
    ['tenant-detail'],
    async () => {
      const token = OrbitSession.get('faberToken')
      const { data } = await B2bTDPAPI.getTDPDetails(token)
      return data
    },
    {
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
      cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
      refetchOnWindowFocus: false
    }
  )

  const { mutate: getAdjustments, isLoading } = useMutation({
    mutationFn: async (searchTerm: IWalletAdjustmentBackendData) => {
      const token = OrbitSession.get('faberToken')
      return WalletAdjustmentApi.getWalletAdjustment(token, searchTerm)
    },
    onSuccess: (data) => {
      if (data?.status === 'success' && data?.data) {
        toast.success('Wallet statement fetched successfully')
        setTableData(data.data)
      } else {
        toast.info('No data found', { autoClose: 3000, hideProgressBar: false })
      }
    },
    onError: () => {
      toast.error('Failed to fetch wallet statement')
    }
  })

  const companyNameInput = watch('companyName')

  const filterOptions = useCallback(
    (options: CompanyOption[], { inputValue }: { inputValue: string }) => {
      if (!inputValue) return options.slice(0, 100) // Limit initial results

      const filtered = options.filter((option) =>
        option.label.toLowerCase().includes(inputValue.toLowerCase())
      )
      return filtered.slice(0, 100)
    },
    []
  )

  const companyOptions = useMemo((): CompanyOption[] => {
    if (!tenantData || !Array.isArray(tenantData)) return []
    return tenantData.map((company: Company) => ({
      label: company.company_name || '',
      value: company.company_name || '',
      companyId: company.id || ''
    }))
  }, [tenantData])

  const onSubmit = (data: FormData) => {
    // Create cleaned data object - only include dates if they are selected
    const cleanedData: IWalletAdjustmentBackendData = {
      company_id: data.companyName?.companyId || ''
      // company_id: data.CompanyID
    }

    // Only include dates if they have values
    if (data.fromDate) {
      cleanedData.form_date = data.fromDate
    }
    if (data.toDate) {
      cleanedData.to_date = data.toDate
    }

    getAdjustments(cleanedData)
  }
  if (tenantFetchError) {
    return (
      <Box>
        <Typography color='error'>Failed to load company data</Typography>
        <Button
          onClick={() => refetchTenant()}
          variant='outlined'
          sx={{ mt: 1 }}
        >
          Retry
        </Button>
      </Box>
    )
  }
  const reset = () => {
    resetSearch()
    setTableData([])
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box>
        <Box
          component='form'
          onSubmit={handleSearchSubmit(onSubmit)}
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 2,
            mb: 2
          }}
        >
          {/* Company Name */}
          <Controller
            name='companyName'
            control={searchControl}
            rules={{
              required: { value: true, message: 'Company name is required' }
            }}
            render={({ field, fieldState: { error } }) => (
              <Autocomplete
                {...field}
                disablePortal
                options={companyOptions}
                filterOptions={filterOptions}
                loading={isTenantLoading}
                getOptionLabel={(option: CompanyOption | null) =>
                  option?.label || ''
                }
                isOptionEqualToValue={(
                  option: CompanyOption,
                  value: CompanyOption | null
                ) => option?.value === value?.value}
                onChange={(_, data: CompanyOption | null) =>
                  field.onChange(data)
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label='Company Name *'
                    placeholder='Select company name'
                    variant='outlined'
                    fullWidth
                    error={!!error}
                    helperText={error?.message}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isTenantLoading ? (
                            <CircularProgress color='inherit' size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      )
                    }}
                  />
                )}
              />
            )}
          />

          {/* From Date */}
          <Controller
            name='fromDate'
            control={searchControl}
            render={({ field }) => (
              <TextField
                {...field}
                type='date'
                label='From Date'
                InputLabelProps={{ shrink: true }}
                variant='outlined'
                fullWidth
              />
            )}
          />

          {/* To Date */}
          <Controller
            name='toDate'
            control={searchControl}
            render={({ field }) => (
              <TextField
                {...field}
                type='date'
                label='To Date'
                InputLabelProps={{ shrink: true }}
                variant='outlined'
                fullWidth
              />
            )}
          />

          {/* Buttons */}
          <Box sx={{ gridColumn: '1 / -1', display: 'flex', gap: 2 }}>
            <Button
              variant='outlined'
              color='secondary'
              onClick={() => reset()}
            >
              Reset
            </Button>
            <Button
              variant='contained'
              color='primary'
              type='submit'
              disabled={!companyNameInput?.value || isLoading}
            >
              {isLoading ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Searching...
                </>
              ) : (
                'Search'
              )}
            </Button>
            <Button
              variant='contained'
              onClick={() => navigate('/hq/b2b/wallet-adjustment/register')}
            >
              Add Wallet Adjustment
            </Button>
          </Box>
        </Box>
      </Box>
      <Box>
        <WalletAdjustmentList data={tableData} />
      </Box>
    </Box>
  )
}
